import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { firebaseApp } from '../../firebase.js';

const db = getFirestore(firebaseApp);

// ========== HELPER FUNCTIONS ==========

/**
 * Generate timeline from application status changes
 */
const generateTimeline = (application) => {
  const timeline = [];

  // Applied
  timeline.push({
    status: 'applied',
    timestamp: application.appliedAt,
    description: `Applied for ${application.jobTitle}`,
    icon: '📝'
  });

  // Test completed
  if (application.testCompletedAt) {
    timeline.push({
      status: 'test_completed',
      timestamp: application.testCompletedAt,
      description: `Completed assessment${application.totalScore ? ` - Scored ${Math.round(application.totalScore)}%` : ''}`,
      icon: '✅'
    });
  }

  // Under review
  if (application.reviewStartedAt) {
    timeline.push({
      status: 'under_review',
      timestamp: application.reviewStartedAt,
      description: 'Application under review by HR',
      icon: '👀'
    });
  }

  // Shortlisted
  if (application.shortlistedAt) {
    timeline.push({
      status: 'shortlisted',
      timestamp: application.shortlistedAt,
      description: 'Shortlisted for further rounds',
      icon: '🎉'
    });
  }

  // Interview scheduled
  if (application.interviewScheduledAt) {
    timeline.push({
      status: 'interview_scheduled',
      timestamp: application.interviewScheduledAt,
      description: `Interview scheduled${application.interviewDate ? ` on ${new Date(application.interviewDate).toLocaleDateString()}` : ''}`,
      icon: '📅'
    });
  }

  // Rejected
  if (application.rejectedAt) {
    timeline.push({
      status: 'rejected',
      timestamp: application.rejectedAt,
      description: application.rejectionReason || 'Application not selected',
      icon: '❌'
    });
  }

  return timeline.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
};

// ========== MAIN FUNCTIONS ==========

/**
 * Track a new job application
 */
export const createApplication = async (
  userId,
  jobId,
  jobTitle,
  hrId,
  hrName
) => {
  try {
    const applicationId = `${userId}_${jobId}`;

    await setDoc(doc(db, 'applications', applicationId), {
      userId,
      jobId,
      jobTitle,
      hrId,
      hrName: hrName || 'Unknown',
      status: 'applied',
      appliedAt: serverTimestamp(),
      lastUpdated: serverTimestamp(),
      timeline: [{
        status: 'applied',
        timestamp: new Date(),
        description: `Applied for ${jobTitle}`,
        icon: '📝'
      }]
    });

    return applicationId;
  } catch (error) {
    console.error('Error creating application:', error);
    throw error;
  }
};

/**
 * Update application status
 */
export const updateApplicationStatus = async (
  applicationId,
  status,
  additionalData
) => {
  try {
    const updateData = {
      status,
      lastUpdated: serverTimestamp(),
      ...additionalData
    };

    // Add status-specific timestamp
    switch (status) {
      case 'test_completed':
        updateData.testCompletedAt = serverTimestamp();
        break;
      case 'under_review':
        updateData.reviewStartedAt = serverTimestamp();
        break;
      case 'shortlisted':
        updateData.shortlistedAt = serverTimestamp();
        break;
      case 'interview_scheduled':
        updateData.interviewScheduledAt = serverTimestamp();
        break;
      case 'rejected':
        updateData.rejectedAt = serverTimestamp();
        break;
    }

    await updateDoc(doc(db, 'applications', applicationId), updateData);
  } catch (error) {
    console.error('Error updating application status:', error);
    throw error;
  }
};

/**
 * Get all applications for a user
 */
export const getUserApplications = async (userId) => {
  try {
    const q = query(
      collection(db, 'applications'),
      where('userId', '==', userId),
      orderBy('appliedAt', 'desc')
    );

    const snapshot = await getDocs(q);

    const applications = snapshot.docs.map(doc => {
      const data = doc.data();
      const app = {
        id: doc.id,
        userId: data.userId,
        jobId: data.jobId,
        testId: data.testId,
        jobTitle: data.jobTitle,
        companyName: data.companyName,
        hrId: data.hrId,
        hrName: data.hrName,
        status: data.status,
        appliedAt: data.appliedAt?.toDate() || new Date(),
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        testScore: data.testScore,
        interviewScore: data.interviewScore,
        totalScore: data.totalScore,
        notes: data.notes,
        timeline: []
      };

      app.timeline = generateTimeline({ ...data, ...app });

      return app;
    });

    return applications;
  } catch (error) {
    console.error('Error fetching applications:', error);
    return [];
  }
};

/**
 * Get application statistics
 */
export const getApplicationStats = async (userId) => {
  try {
    const applications = await getUserApplications(userId);

    const stats = {
      total: applications.length,
      applied: applications.filter(a => a.status === 'applied').length,
      testCompleted: applications.filter(a => a.status === 'test_completed').length,
      underReview: applications.filter(a => a.status === 'under_review').length,
      shortlisted: applications.filter(a => a.status === 'shortlisted').length,
      rejected: applications.filter(a => a.status === 'rejected').length,
      interviewScheduled: applications.filter(a => a.status === 'interview_scheduled').length,
      avgResponseTime: 0,
      successRate: 0
    };

    // Calculate average response time (days from application to any status change)
    const responseTimes = applications
      .filter(a => a.timeline.length > 1)
      .map(a => {
        const applied = a.timeline[0].timestamp;
        const responded = a.timeline[1].timestamp;
        return (responded.getTime() - applied.getTime()) / (1000 * 60 * 60 * 24);
      });

    stats.avgResponseTime = responseTimes.length > 0
      ? Math.round(responseTimes.reduce((sum, t) => sum + t, 0) / responseTimes.length)
      : 0;

    // Success rate = (shortlisted + interview_scheduled) / total
    const successful = stats.shortlisted + stats.interviewScheduled;
    stats.successRate = stats.total > 0
      ? Math.round((successful / stats.total) * 100)
      : 0;

    return stats;
  } catch (error) {
    console.error('Error calculating application stats:', error);
    return {
      total: 0,
      applied: 0,
      testCompleted: 0,
      underReview: 0,
      shortlisted: 0,
      rejected: 0,
      interviewScheduled: 0,
      avgResponseTime: 0,
      successRate: 0
    };
  }
};

/**
 * Link test result to application
 */
export const linkTestToApplication = async (
  userId,
  testId,
  jobId,
  score,
  totalScore
) => {
  try {
    const applicationId = `${userId}_${jobId}`;
    const appDoc = await getDoc(doc(db, 'applications', applicationId));

    if (appDoc.exists()) {
      await updateApplicationStatus(applicationId, 'test_completed', {
        testId,
        testScore: score,
        totalScore
      });
    } else {
      // Create application if it doesn't exist (retroactive)
      await createApplication(userId, jobId, 'Assessment', 'unknown');
      await updateApplicationStatus(applicationId, 'test_completed', {
        testId,
        testScore: score,
        totalScore
      });
    }
  } catch (error) {
    console.error('Error linking test to application:', error);
  }
};

/**
 * Sync applications from test results (for retroactive data)
 */
export const syncApplicationsFromTests = async (userId) => {
  try {
    const testsQuery = query(
      collection(db, 'jobseekerResults'),
      where('jobSeekerId', '==', userId)
    );

    const snapshot = await getDocs(testsQuery);
    let synced = 0;

    for (const testDoc of snapshot.docs) {
      const data = testDoc.data();
      const applicationId = `${userId}_${data.testId}`;

      // Check if application already exists
      const appDoc = await getDoc(doc(db, 'applications', applicationId));

      if (!appDoc.exists()) {
        await setDoc(doc(db, 'applications', applicationId), {
          userId,
          jobId: data.testId,
          testId: data.testId,
          jobTitle: data.jobTitle || 'Assessment',
          hrId: data.hrId || 'unknown',
          hrName: 'Unknown',
          status: 'test_completed',
          appliedAt: data.completedAt || serverTimestamp(),
          lastUpdated: serverTimestamp(),
          testScore: data.score,
          interviewScore: data.interviewScore,
          totalScore: data.totalScore,
          testCompletedAt: data.completedAt || serverTimestamp()
        });

        synced++;
      }
    }

    return synced;
  } catch (error) {
    console.error('Error syncing applications:', error);
    return 0;
  }
};

